import React, { useEffect, useRef, useState } from 'react';
import DateRangePicker from './DateRangePicker';
import FilterChips from './FilterChips';
import { formatDateUser } from '../../utils/dateUtils';

const TaskFilters = ({ 
  filters,
  onFilterChange,
  departments = [],
  employees = [],
  customers = [],
  supervisors = [],
  filterChips,
  onClearAll,
  currentUser = null,
  allowOverdueFilter = true
}) => {
  const {
    filterStatus,
    selectedDepartment,
    selectedEmployee,
    selectedCustomer,
    selectedSupervisor,
    showOverdueOnly,
    selectedDateRange
  } = filters;

  const {
    handleFilterChange,
    handleDepartmentChange,
    handleEmployeeChange,
    handleCustomerChange,
    handleSupervisorChange,
    handleOverdueFilterChange,
    handleDateFilterChange
  } = onFilterChange;

  // Check if current user can see supervisor filter
  const canShowSupervisorFilter = currentUser && (currentUser.role === 'supervisor' || currentUser.role === 'admin');

  const [showRangePicker, setShowRangePicker] = useState(false);
  const pickerRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const activeFilterChips = Array.isArray(filterChips) ? filterChips : [];
  const hasActiveFilters = activeFilterChips.length > 0;
  const activeFilterCountLabel = hasActiveFilters ? `${activeFilterChips.length} active` : 'No filters';

  useEffect(() => {
    if (!showRangePicker) return;
    const onClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowRangePicker(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setShowRangePicker(false);
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showRangePicker]);

  // Auto-collapse filters whenever any filter value changes
  useEffect(() => {
    setIsExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, selectedDepartment, selectedEmployee, selectedCustomer, selectedSupervisor, selectedDateRange, showOverdueOnly]);

  // Ensure Clear collapses immediately then triggers onClearAll
  const handleClearAllClick = () => {
    setIsExpanded(false);
    if (typeof onClearAll === 'function') onClearAll();
  };

  return (
    <div className={`desktop-filters ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="tasks-filter-panel">
        <div className="filter-panel-header">
          <button
            type="button"
            className="filter-toggle-button"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
          >
            <span>Filters</span>
            <span className="filter-toggle-icon" aria-hidden="true">
              {isExpanded ? '▲' : '▼'}
            </span>
          </button>
          <div className="filter-panel-actions">
            <span className="filter-count">{activeFilterCountLabel}</span>
            {hasActiveFilters && (
              <button type="button" className="filter-summary-clear" onClick={handleClearAllClick}>
                Clear
              </button>
            )}
          </div>
        </div>

        {!isExpanded && hasActiveFilters && (
          <div className="filter-panel-footer">
            <FilterChips chips={activeFilterChips} onClearAll={handleClearAllClick} showClearAll={false} />
          </div>
        )}

        {isExpanded && (
          <>
            <div className="filter-options compact-grid">
              {/* Status Filter */}
              <div className="filter-group">
                <label>Status</label>
                <div className="filter-chips-horizontal">
                  <button 
                    className={`filter-chip-option ${filterStatus.length === 0 ? 'active' : ''}`}
                    onClick={() => handleFilterChange('all')}
                  >
                    All
                  </button>
                  <button 
                    className={`filter-chip-option ${filterStatus.includes('incoming') ? 'active' : ''}`}
                    onClick={() => handleFilterChange('incoming')}
                  >
                    Incoming
                  </button>
                  <button 
                    className={`filter-chip-option ${filterStatus.includes('in-progress') ? 'active' : ''}`}
                    onClick={() => handleFilterChange('in-progress')}
                  >
                    In Progress
                  </button>
                  <button 
                    className={`filter-chip-option ${filterStatus.includes('resolved') ? 'active' : ''}`}
                    onClick={() => handleFilterChange('resolved')}
                  >
                    Resolved
                  </button>
                  <button 
                    className={`filter-chip-option ${filterStatus.includes('rejected') ? 'active' : ''}`}
                    onClick={() => handleFilterChange('rejected')}
                  >
                    Rejected
                  </button>
                  {allowOverdueFilter && (
                    <button
                      className={`filter-chip-option ${showOverdueOnly ? 'active' : ''}`}
                      onClick={() => handleOverdueFilterChange(!showOverdueOnly)}
                      title="Show tasks past their due date"
                    >
                      Overdue
                    </button>
                  )}
                </div>
              </div>

              {/* Supervisor Filter - Show if user is supervisor or admin */}
              {canShowSupervisorFilter && (
                <div className="filter-group">
                  <label>Supervisor</label>
                  <div className="filter-chips-horizontal">
                    <button 
                      className={`filter-chip-option ${!selectedSupervisor ? 'active' : ''}`}
                      onClick={() => handleSupervisorChange(null)}
                    >
                      All
                    </button>
                    <button 
                      className={`filter-chip-option ${selectedSupervisor === currentUser?.id ? 'active' : ''}`}
                      onClick={() => handleSupervisorChange(currentUser?.id)}
                    >
                      My Tasks
                    </button>
                    {supervisors
                      .filter(supervisor => supervisor.id !== currentUser?.id) // Don't show current user twice
                      .map(supervisor => (
                        <button 
                          key={supervisor.id}
                          className={`filter-chip-option ${selectedSupervisor === supervisor.id ? 'active' : ''}`}
                          onClick={() => handleSupervisorChange(supervisor.id)}
                        >
                          {supervisor.firstName} {supervisor.lastName}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Department Filter */}
              <div className="filter-group">
                <label>Department</label>
                <div className="filter-chips-horizontal">
                  <button 
                    className={`filter-chip-option ${!selectedDepartment ? 'active' : ''}`}
                    onClick={() => handleDepartmentChange(null)}
                  >
                    All
                  </button>
                  {departments.map(dept => (
                    <button 
                      key={dept.id}
                      className={`filter-chip-option ${selectedDepartment === dept.id ? 'active' : ''}`}
                      onClick={() => handleDepartmentChange(dept.id)}
                    >
                      {dept.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Employee Filter */}
              <div className="filter-group">
                <label>Employee</label>
                <div className="filter-chips-horizontal">
                  <button 
                    className={`filter-chip-option ${!selectedEmployee ? 'active' : ''}`}
                    onClick={() => handleEmployeeChange(null)}
                  >
                    All
                  </button>
                  {employees.map(emp => (
                    <button 
                      key={emp.id}
                      className={`filter-chip-option ${selectedEmployee === emp.id ? 'active' : ''}`}
                      onClick={() => handleEmployeeChange(emp.id)}
                    >
                      {emp.firstName} {emp.lastName}
                    </button>
                  ))}
                </div>
              </div>

              {/* Customer Filter */}
              <div className="filter-group">
                <label>Customer</label>
                <div className="filter-chips-horizontal">
                  <button 
                    className={`filter-chip-option ${!selectedCustomer ? 'active' : ''}`}
                    onClick={() => handleCustomerChange(null)}
                  >
                    All
                  </button>
                  {customers.map(customer => (
                    <button 
                      key={customer.id}
                      className={`filter-chip-option ${selectedCustomer === customer.id ? 'active' : ''}`}
                      onClick={() => handleCustomerChange(customer.id)}
                    >
                      {customer.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range Filter */}
            {/*   <div className="filter-group date-filter-group">
                <label>Date</label>
                <div className="date-range-fields">
                  <button className="drp-input" onClick={(e) => { e.stopPropagation(); setShowRangePicker((s) => (s ? false : true)); }} aria-label="Select start date">
                    {selectedDateRange?.startDate ? formatDateUser(selectedDateRange.startDate) : 'Start date'}
                  </button>
                  <span className="date-separator">-</span>
                  <button className="drp-input" onClick={(e) => { e.stopPropagation(); setShowRangePicker((s) => (s ? false : true)); }} aria-label="Select end date">
                    {selectedDateRange?.endDate ? formatDateUser(selectedDateRange.endDate) : 'End date'}
                  </button>
                  {selectedDateRange && (
                    <button className="btn-secondary small drp-clear" onClick={() => handleDateFilterChange('all')} aria-label="Clear date range">
                      Clear
                    </button>
                  )}
                </div>
                {showRangePicker && (
                  <div ref={pickerRef}>
                    <DateRangePicker 
                      value={selectedDateRange}
                      onChange={(range) => { setShowRangePicker(false); handleDateFilterChange(range); }}
                    />
                  </div>
                )}
              </div> */}
            </div>

            {hasActiveFilters && (
              <div className="filter-panel-footer">
                <FilterChips chips={activeFilterChips} onClearAll={onClearAll} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TaskFilters; 